// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import logger from '@/lib/logger';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

// Check if environment variables are available
if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  logger.error('Missing Supabase configuration', {
    module: 'Supabase',
    data: { 
      hasUrl: !!SUPABASE_URL, 
      hasKey: !!SUPABASE_PUBLISHABLE_KEY 
    }
  });
  throw new Error('Supabase configuration is missing. Check your environment variables.');
}

export const supabase = createClient<Database>(
  SUPABASE_URL, 
  SUPABASE_PUBLISHABLE_KEY,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      storageKey: 'vibebops-auth',
    },
    global: {
      headers: {
        'X-Client-Info': 'vibebops-app',
      },
    },
    // Add retry options for better resilience
    db: {
      schema: 'public',
    },
    realtime: {
      timeout: 10000, // Increase timeout for realtime connections
    },
  }
);

// Add these variables at the top of the file, after the imports
let isOfflineMode = false;
let lastConnectionAttempt = 0;
const CONNECTION_RETRY_INTERVAL = 30000; // 30 seconds
let visibilityReconnectTimer: number | null = null;

// Add visibility change handler to refresh the connection when tab becomes visible
document.addEventListener('visibilitychange', async () => {
  if (document.visibilityState === 'visible') {
    logger.info('Page became visible, ensuring connection...', { module: 'Supabase' });
    
    // Clear any previous visibility reconnect timer
    if (visibilityReconnectTimer !== null) {
      clearTimeout(visibilityReconnectTimer);
    }
    
    // Start a reconnection process with multiple attempts after the tab becomes visible
    const attemptReconnection = async (attemptsRemaining = 3, delayMs = 1000) => {
      try {
        // First check for a session
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          logger.warn('Error getting session on visibility change', { 
            module: 'Supabase', 
            data: error 
          });
        }
        
        if (data?.session) {
          logger.info('Session exists, refreshing auth state...', { module: 'Supabase' });
          // Refresh the session
          await supabase.auth.refreshSession();
          
          // Test connection to ensure it's actually working
          const connectionTest = await testConnection();
          if (!connectionTest.connected && attemptsRemaining > 0) {
            logger.warn(`Reconnection attempt failed, ${attemptsRemaining} attempts remaining`, { 
              module: 'Supabase' 
            });
            // Try again after a delay
            visibilityReconnectTimer = window.setTimeout(() => {
              attemptReconnection(attemptsRemaining - 1, delayMs * 1.5);
            }, delayMs);
          } else if (connectionTest.connected) {
            logger.info('Reconnection successful on visibility change', { module: 'Supabase' });
            // Force a page refresh if there were previous unsuccessful attempts
            if (attemptsRemaining < 3) {
              logger.info('Refreshing page to ensure consistent state after reconnection', { 
                module: 'Supabase' 
              });
              window.location.reload();
            }
          }
        } else if (attemptsRemaining > 0) {
          // No session but we can still try to restore connection
          logger.warn('No session found on visibility change, attempting reconnect...', { 
            module: 'Supabase' 
          });
          visibilityReconnectTimer = window.setTimeout(() => {
            attemptReconnection(attemptsRemaining - 1, delayMs * 1.5);
          }, delayMs);
        }
      } catch (error) {
        logger.error('Error refreshing connection on visibility change', {
          module: 'Supabase',
          data: error
        });
        
        // Still try again if we have attempts remaining
        if (attemptsRemaining > 0) {
          visibilityReconnectTimer = window.setTimeout(() => {
            attemptReconnection(attemptsRemaining - 1, delayMs * 1.5);
          }, delayMs);
        }
      }
    };
    
    // Start the reconnection process
    attemptReconnection();
  }
});

/**
 * Enable offline mode for the app
 * This allows the app to continue operating with limited functionality
 */
export function enableOfflineMode() {
  if (!isOfflineMode) {
    logger.warn('Switching to offline mode', { module: 'Supabase' });
    isOfflineMode = true;
  }
}

/**
 * Check if the app is in offline mode
 */
export function isInOfflineMode() {
  return isOfflineMode;
}

// Add a connection health checker that runs periodically
let connectionHealthInterval: number | null = null;

// Start monitoring connection health
export function startConnectionHealthCheck(intervalMs = 60000) {
  // Clear any existing interval
  if (connectionHealthInterval) {
    clearInterval(connectionHealthInterval);
  }
  
  // Set up new health check interval
  connectionHealthInterval = window.setInterval(async () => {
    try {
      await pingConnection();
    } catch (err) {
      logger.error('Connection health check failed', {
        module: 'Supabase',
        data: err
      });
    }
  }, intervalMs);
  
  // Start immediately
  pingConnection().catch(err => {
    logger.error('Initial connection health check failed', {
      module: 'Supabase',
      data: err
    });
  });
  
  return () => {
    if (connectionHealthInterval) {
      clearInterval(connectionHealthInterval);
      connectionHealthInterval = null;
    }
  };
}

// Call this function when the app initializes
document.addEventListener('DOMContentLoaded', () => {
  startConnectionHealthCheck();
});

/**
 * Test connection to Supabase
 * @returns {Promise<{connected: boolean, error?: string, data?: any}>}
 */
export async function testConnection() {
  try {
    // Longer timeout for production environments
    const timeoutMs = 5000;
    
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Connection test timed out')), timeoutMs);
    });
    
    // We'll skip the ping test via fetch since it may fail in some environments
    // but still allows proper database connections. Instead, go directly to a query test.
    
    // First try with a lightweight timeout-protected query
    try {
      // Now try an actual query
      const queryPromise = supabase.from('profiles').select('count').limit(1);
      
      // Race between the query and the timeout
      const { data, error } = await Promise.race([
        queryPromise,
        timeoutPromise.then(() => {
          throw new Error('Connection test timed out');
        })
      ]) as any;
      
      if (error) {
        // Check if it's an auth error vs. a connection error
        if (error.message?.includes('auth') || error.message?.includes('JWT')) {
          // Auth errors can happen but still indicate the server is reachable
          return {
            connected: true,
            error: error.message,
            authIssue: true
          };
        }
        
        return {
          connected: false,
          error: error.message
        };
      }
      
      return {
        connected: true,
        data
      };
    } catch (queryErr) {
      logger.error('Query test failed:', {
        module: 'Supabase',
        data: queryErr
      });
      
      // If we still fail, try a simple auth check as a fallback
      // This might work even if database queries fail
      try {
        const { data: authData, error: authError } = await supabase.auth.getSession();
        
        // If auth check succeeds, at least some Supabase services are available
        if (!authError) {
          return {
            connected: true,
            data: { auth: 'available' },
            partial: true // Flag indicating only partial connectivity
          };
        }
        
        // Otherwise pass through the error
        return {
          connected: false,
          error: authError?.message || 'Authentication service not available'
        };
      } catch (authErr) {
        return {
          connected: false,
          error: authErr instanceof Error ? authErr.message : 'Complete connection failure'
        };
      }
    }
  } catch (err) {
    return {
      connected: false,
      error: err instanceof Error ? err.message : 'Unknown error'
    };
  }
}

/**
 * Function to ping Supabase and check if the connection is alive
 * @returns {Promise<{connected: boolean, responseTime?: number, error?: string}>}
 */
export async function pingConnection() {
  try {
    // Make a lightweight query to check connection
    const start = Date.now();
    const { data, error } = await supabase.from('profiles').select('count').limit(1);
    const responseTime = Date.now() - start;

    if (error) {
      logger.warn('Connection ping failed', {
        module: 'Supabase',
        data: { error, responseTime }
      });
      return { connected: false, responseTime, error: error.message };
    }

    logger.info(`Connection ping successful. Response time: ${responseTime}ms`, {
      module: 'Supabase'
    });
    return { connected: true, responseTime };
  } catch (err) {
    logger.error('Connection ping error', {
      module: 'Supabase',
      data: err
    });
    return { 
      connected: false,
      error: err instanceof Error ? err.message : 'Unknown error'
    };
  }
}

/**
 * Function to maintain connection or reconnect
 * @returns {Promise<boolean>} Whether the connection is active
 */
export async function ensureConnection() {
  const { connected } = await pingConnection();
  if (!connected) {
    logger.info('Connection appears to be stale, attempting to refresh...', {
      module: 'Supabase'
    });
    
    try {
      // Try to refresh the session
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        logger.error('Session refresh failed', {
          module: 'Supabase',
          data: error
        });
        return false;
      }
      
      logger.info('Session refreshed successfully', {
        module: 'Supabase'
      });
      return true;
    } catch (error) {
      logger.error('Error refreshing connection', {
        module: 'Supabase',
        data: error
      });
      return false;
    }
  }
  return true;
}
